/**
 * 관리자 가이드 페이지 공통 쉘 컨트롤러 (헬퍼).
 *
 * 페이지 디스패치는 PHP 컨트롤러 짧은 이름(`AdminController`)으로 처리되므로
 * 이 파일은 ControllerRegistry 에 등록되지 않는 **내부 헬퍼**다.
 * AdminController.ts 의 guideBrand / guideStyle / guideComponents 메소드가
 * `AdminGuideController.mountShell()` 을 호출해 공통 기능을 활성화한다.
 *
 * 담당:
 *   - 우측 미니 TOC (On this page) 자동 생성 + IntersectionObserver scroll-spy
 *   - 좌측 사이드바 링크 활성화 (페이지 내 앵커 기준)
 *   - 코드 토글 (.guide-demo[data-mode=preview|code])
 *   - 클립보드 복사 ([data-guide-copy])
 *   - 모바일 사이드바 off-canvas 토글 ([data-guide-nav-toggle])
 *   - 앵커 스무스 스크롤 (prefers-reduced-motion 존중)
 */
import { Logger } from "../../utils/Logger";

interface GuideShellOptions {
    tocSelector?: string;
    sectionSelector?: string;
    rootMargin?: string;
}

const DEFAULTS: Required<GuideShellOptions> = {
    tocSelector: "[data-guide-toc]",
    sectionSelector: "[data-guide-section][id]",
    rootMargin: "-80px 0px -55% 0px",
};

let shellInited = false;

/**
 * 스무스 스크롤이 허용되는지 (prefers-reduced-motion: no-preference).
 */
function prefersSmooth(): boolean {
    return !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * 모든 .guide-section[id] 를 스캔해 우측 TOC ul 을 채운다.
 */
function buildToc(tocList: HTMLElement, sections: HTMLElement[]): HTMLAnchorElement[] {
    tocList.innerHTML = "";
    const links: HTMLAnchorElement[] = [];
    sections.forEach((sec) => {
        const id = sec.id;
        const titleEl = sec.querySelector<HTMLElement>(".guide-section__title");
        const label = (titleEl?.textContent || sec.dataset.guideTitle || id).trim();

        const li = document.createElement("li");
        li.className = "guide-toc__item";
        const a = document.createElement("a");
        a.className = "guide-toc__link";
        a.href = `#${id}`;
        a.dataset.guideTocLink = id;
        a.innerHTML = `<span class="guide-toc__bullet" aria-hidden="true"></span><span class="guide-toc__label"></span>`;
        const labelEl = a.querySelector<HTMLElement>(".guide-toc__label");
        if (labelEl) labelEl.textContent = label;
        li.appendChild(a);
        tocList.appendChild(li);
        links.push(a);
    });
    return links;
}

/**
 * scroll-spy 의 현재 활성 섹션 id 를 좌측 nav / 우측 toc 두 곳에 동기화.
 */
function setActiveAnchor(
    id: string,
    tocLinks: HTMLAnchorElement[],
    navLinks: HTMLAnchorElement[],
): void {
    const apply = (a: HTMLAnchorElement, key: string) => {
        const target = a.dataset.guideTocLink || a.dataset.guideNavLink || "";
        const isActive = target === key;
        a.classList.toggle("is-active", isActive);
        if (isActive) {
            a.setAttribute("aria-current", "true");
        } else {
            a.removeAttribute("aria-current");
        }
    };
    tocLinks.forEach((a) => apply(a, id));
    navLinks.forEach((a) => apply(a, id));
}

/**
 * 앵커 링크 클릭 시 스무스 스크롤 + 해시 업데이트.
 */
function attachAnchorScroll(links: HTMLAnchorElement[]): void {
    const smooth = prefersSmooth();
    links.forEach((a) => {
        a.addEventListener("click", (e) => {
            const href = a.getAttribute("href") || "";
            if (!href.startsWith("#")) return;
            const id = href.slice(1);
            const target = document.getElementById(id);
            if (!target) return;
            e.preventDefault();
            target.scrollIntoView({
                behavior: smooth ? "smooth" : "auto",
                block: "start",
            });
            if (window.history && typeof window.history.replaceState === "function") {
                window.history.replaceState(null, "", `#${id}`);
            }
            // 모바일에서 클릭 시 사이드바 닫기
            const layout = document.querySelector(".guide-layout");
            if (layout?.classList.contains("guide-layout--nav-open")) {
                layout.classList.remove("guide-layout--nav-open");
                document.body.classList.remove("guide-nav-open");
            }
        });
    });
}

/**
 * 코드 토글 (preview ↔ code).
 */
function attachCodeToggle(): void {
    document.querySelectorAll<HTMLElement>(".guide-demo").forEach((demo) => {
        const buttons = demo.querySelectorAll<HTMLButtonElement>("[data-guide-toggle]");
        if (buttons.length === 0) return;
        buttons.forEach((btn) => {
            btn.addEventListener("click", () => {
                const mode = btn.dataset.guideToggle || "preview";
                demo.dataset.mode = mode;
                buttons.forEach((b) =>
                    b.classList.toggle("is-active", b.dataset.guideToggle === mode),
                );
            });
        });
    });
}

/**
 * [data-guide-copy] 클릭 시 인접 <pre> 또는 data-copy 속성을 클립보드에 복사.
 */
function attachCopyButtons(): void {
    document.querySelectorAll<HTMLButtonElement>("[data-guide-copy]").forEach((btn) => {
        btn.addEventListener("click", async () => {
            let text = btn.dataset.copy || "";
            if (!text) {
                // 인접한 .guide-demo 또는 자기 부모 안의 <pre> 텍스트를 찾는다.
                const scope = btn.closest<HTMLElement>(".guide-demo, .guide-token-row") || document;
                const pre = scope.querySelector<HTMLElement>("pre code, pre");
                if (pre) text = pre.textContent || "";
            }
            if (!text) return;
            try {
                await navigator.clipboard.writeText(text);
                btn.classList.add("is-copied");
                window.setTimeout(() => btn.classList.remove("is-copied"), 1500);
            } catch (err) {
                Logger.debug("guide copy failed", err);
            }
        });
    });
}

/**
 * 모바일 사이드바 off-canvas 토글.
 */
function attachNavToggle(): void {
    const layout = document.querySelector<HTMLElement>(".guide-layout");
    if (!layout) return;
    const triggers = document.querySelectorAll<HTMLButtonElement>("[data-guide-nav-toggle]");
    triggers.forEach((btn) => {
        btn.addEventListener("click", () => {
            const next = !layout.classList.contains("guide-layout--nav-open");
            layout.classList.toggle("guide-layout--nav-open", next);
            document.body.classList.toggle("guide-nav-open", next);
            triggers.forEach((t) => {
                t.setAttribute("aria-expanded", next ? "true" : "false");
                t.hidden = false;
            });
        });
    });

    // 콘텐츠 영역 클릭 시 자동 닫힘 (모바일에서만)
    layout.addEventListener("click", (e) => {
        if (!layout.classList.contains("guide-layout--nav-open")) return;
        const target = e.target as HTMLElement;
        if (target.closest("[data-guide-nav-toggle]") || target.closest("[data-guide-nav]")) return;
        layout.classList.remove("guide-layout--nav-open");
        document.body.classList.remove("guide-nav-open");
        triggers.forEach((t) => t.setAttribute("aria-expanded", "false"));
    });
}

export const AdminGuideController = {
    /**
     * 가이드 3페이지 공통 진입점.
     * 페이지마다 .guide-layout / [data-guide-toc] / [data-guide-section] 이
     * 마크업되어 있어야 동작한다. 마크업이 없으면 무해하게 no-op.
     */
    mountShell(options: GuideShellOptions = {}): void {
        if (shellInited) return;
        const opts = { ...DEFAULTS, ...options };

        const layout = document.querySelector<HTMLElement>(".guide-layout");
        if (!layout) {
            Logger.debug("AdminGuideController.mountShell: .guide-layout 없음, skip");
            return;
        }

        const sections = Array.from(
            document.querySelectorAll<HTMLElement>(opts.sectionSelector),
        );
        const tocList = document.querySelector<HTMLElement>(opts.tocSelector);
        const navLinks = Array.from(
            document.querySelectorAll<HTMLAnchorElement>("[data-guide-nav-link]"),
        );

        const tocLinks = tocList ? buildToc(tocList, sections) : [];

        attachAnchorScroll([...tocLinks, ...navLinks]);
        attachCodeToggle();
        attachCopyButtons();
        attachNavToggle();

        if (sections.length > 0 && "IntersectionObserver" in window) {
            const active: Record<string, IntersectionObserverEntry> = {};
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            active[(entry.target as HTMLElement).id] = entry;
                        } else {
                            delete active[(entry.target as HTMLElement).id];
                        }
                    });
                    const visible = Object.values(active).sort(
                        (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
                    );
                    const topId = visible[0]?.target?.id;
                    if (topId) setActiveAnchor(topId, tocLinks, navLinks);
                },
                { rootMargin: opts.rootMargin, threshold: [0, 0.25, 0.5, 1] },
            );
            sections.forEach((sec) => observer.observe(sec));
        }

        // 초기 hash 가 있으면 해당 섹션으로 이동
        const initialHash = window.location.hash.replace("#", "");
        if (initialHash) {
            const target = document.getElementById(initialHash);
            if (target) {
                window.requestAnimationFrame(() => {
                    target.scrollIntoView({ behavior: "auto", block: "start" });
                });
            }
        }

        shellInited = true;
    },
};
